import React, { useMemo } from 'react'
import HorizontalScroll from '@/components/atoms/HorizontalScroll/HorizontalScroll'
import NewsItem from '../NewsItem/NewsItem'
import SegmentCardTracker from '@/components/organisms/CenterCards/SegmentCardTracker'
import { isVerified, isPPV, getThirdPartyReviews } from '@/domain/center'
import HorizontalScrollLoader from '@/components/atoms/HorizontalScroll/HorizontalScrollLoader'
import { isEmptyArray, isNonEmptyString } from '@/utils/checks'
import { Center } from '@/types/models'
import Container from '@/components/Container'
import Section from '@/components/Section'
import { THtmlElementHeading } from '@/types/HtmlElements'
import areAllCentersSponsor from '@/domain/useCases/center/areAllCentersSponsor'
import getCenterCarouselImageUrl from '@/domain/center/useCases/getCenterCarouselImageUrl'
import CenterCarouselHeader from './CenterCarouselHeader'
import CenterCarouselFooter from './CenterCarouselFooter'

export type HeaderWeight = 'normal' | 'medium' | 'semibold' | 'bold'
export type HeaderSize =
  | '6xl'
  | '5xl'
  | '4xl'
  | '3xl'
  | '2xl'
  | 'xl'
  | 'lg'
  | 'md'
  | 'sm'
  | 'base'

interface CenterCarouselProps {
  (props: {
    className?: string
    centers: Center[]
    containerId?: string
    zoneId: number | string
    isLoading: boolean
    feedPosition?: number
    scrollItemCount?: number
    gridClassName?: string
    hideDescription?: boolean
    isInfeed?: boolean
    sectionHeading?: string
    sectionSubHeading?: string
    sectionSize?: string
    desktopCentered?: boolean
    alwaysScroll?: boolean
    HeaderTag?: THtmlElementHeading
    headerAlignment?: 'left' | 'center' | 'right'
    headerSize?: HeaderSize
    showAdsDisclaimer?: boolean
    onCenterClick?: (center: Center, index: number) => void
    headerWeight?: HeaderWeight
    useFieldsVariable?: boolean
    withGradient?: boolean
    noPadding?: boolean
    SubHeaderTag?: THtmlElementHeading
    subheaderAlignment?: 'left' | 'center' | 'right'
    subheaderSize?: HeaderSize
    subheaderWeight?: HeaderWeight
    termTitle?: string
    shouldHideAdTags?: boolean
    headerLink?: string
    headerLinkText?: string
  }): JSX.Element | null
}

const CenterCarousel: CenterCarouselProps = ({
  className = '',
  centers = [] as Center[],
  containerId,
  zoneId,
  isLoading = true,
  feedPosition,
  scrollItemCount = 4,
  gridClassName = '',
  hideDescription = false,
  isInfeed = false,
  sectionSize = '',
  desktopCentered = false,
  sectionHeading = '',
  HeaderTag = 'p',
  headerAlignment = 'left',
  headerSize = 'lg',
  headerWeight = 'medium',
  sectionSubHeading = '',
  SubHeaderTag = 'p',
  useFieldsVariable = false,
  withGradient = true,
  noPadding = false,
  termTitle = '',
  shouldHideAdTags = false,
  onCenterClick,
  headerLink,
  headerLinkText
}) => {
  if (!isLoading && isEmptyArray(centers)) {
    return null
  }

  const allCentersArePPV = useMemo(() => {
    return areAllCentersSponsor(centers) && !shouldHideAdTags
  }, [centers])

  const shouldShowCta = useMemo(() => {
    return isNonEmptyString(headerLink) && isNonEmptyString(headerLinkText)
  }, [headerLink, headerLinkText])

  return (
    <Section
      size={sectionSize}
      className={className}
    >
      <Container noPadding={noPadding}>
        {isInfeed && (
          <hr className="border-t-1 mb-6 mt-8 !h-0 border-gray-200" />
        )}

        {isNonEmptyString(sectionHeading) && (
          <CenterCarouselHeader
            centers={centers}
            isLoading={isLoading}
            sectionHeading={sectionHeading}
            HeaderTag={HeaderTag}
            headerAlignment={headerAlignment}
            headerSize={headerSize}
            headerWeight={headerWeight}
            sectionSubHeading={sectionSubHeading}
            SubHeaderTag={SubHeaderTag}
            useFieldsVariable={useFieldsVariable}
            allCentersArePPV={allCentersArePPV}
            shouldShowCta={shouldShowCta}
            headerLink={headerLink}
            headerLinkText={headerLinkText}
            shouldHideAdTags={shouldHideAdTags}
          />
        )}

        <HorizontalScroll
          id={containerId}
          count={scrollItemCount}
          gridClassName={gridClassName}
          desktopCentered={desktopCentered}
          withGradient={withGradient}
        >
          {isLoading ? (
            <HorizontalScrollLoader hideDescription={hideDescription} />
          ) : (
            centers.map((center, i) => {
              const isSponsorCenter = isPPV(center) && !shouldHideAdTags
              return (
                <SegmentCardTracker
                  center={center}
                  position={feedPosition}
                  positionInElement={i + 1}
                  zone={zoneId}
                  key={center.slug}
                >
                  <NewsItem
                    center={center}
                    zoneId={zoneId}
                    hasCertifications={isVerified(center)}
                    onClick={() => onCenterClick?.(center, i)}
                    image={getCenterCarouselImageUrl(center)}
                    title={center.title}
                    noReadMore={true}
                    sponsor={isSponsorCenter}
                    subtitle={center.location_label}
                    link={`/${center.slug}/`}
                    url={`${center.slug}`}
                    reviewCount={center.review_count}
                    reviewAverage={center.review_average}
                    thirdPartyReviews={getThirdPartyReviews(center)}
                    hideDescription={hideDescription}
                    shouldDefaultRegionalize={false}
                    providerType={center.providerType}
                    price={center.pricing_label}
                    pricing={center.price || []}
                    acceptedInsurance={center.accepted_insurance}
                    insuranceDescription={center.insurance_description}
                    verifyInsuranceLink={center.verify_insurance_link}
                    showAdBadge={!allCentersArePPV && isSponsorCenter}
                    overrideAdBadge={shouldHideAdTags}
                    featuredTerm={termTitle}
                  />
                </SegmentCardTracker>
              )
            })
          )}
        </HorizontalScroll>
        <CenterCarouselFooter
          shouldShowCta={shouldShowCta}
          headerLink={headerLink}
          headerLinkText={headerLinkText}
        />
        {isInfeed && <hr className="border-t-1 mt-8 !h-0 border-gray-200" />}
      </Container>
    </Section>
  )
}

export default CenterCarousel
